<script setup lang="ts">
import { logger } from '@/utils/logger';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { computed, ref, watch } from 'vue';
import { getDraftingStatistics, type DraftingStatistics } from '@/api';
import StatisticsKpiGrid, {
  type StatisticsKpiTile,
} from '@/components/admin/StatisticsKpiGrid.vue';
import {
  buildStatisticsDateQuery,
  formatStatisticsNumber,
  type StatisticsFiltersProps,
} from '@/utils/adminStatistics';

const props = defineProps<StatisticsFiltersProps>();
const message = useGaiaToast();

const data = ref<DraftingStatistics | undefined>(undefined);
const loading = ref(false);

// Template-side counts and draft-side counts are scoped to rows created in the
// window, so the tiles keep the two groups adjacent rather than interleaved.
const kpiTiles = computed<StatisticsKpiTile[]>(() => {
  if (data.value === undefined) {
    return [];
  }

  return [
    { label: 'Total templates', value: formatStatisticsNumber(data.value.total_templates) },
    {
      label: 'Total variants',
      value: formatStatisticsNumber(data.value.total_template_variants),
    },
    {
      label: 'Avg variants / template',
      value: formatStatisticsNumber(data.value.average_variants_per_template),
    },
    {
      label: 'Total questions',
      value: formatStatisticsNumber(data.value.total_template_questions),
    },
    {
      label: 'Avg questions / template',
      value: formatStatisticsNumber(data.value.average_questions_per_template),
    },
    { label: 'Total drafts', value: formatStatisticsNumber(data.value.total_drafts) },
    { label: 'Saved drafts', value: formatStatisticsNumber(data.value.total_saved_drafts) },
    {
      label: 'Avg drafts / template',
      value: formatStatisticsNumber(data.value.average_drafts_per_template),
    },
  ];
});

async function fetchStatistics(): Promise<void> {
  loading.value = true;
  try {
    const response = await getDraftingStatistics({
      query: {
        included_org_uids: props.includedOrgUids,
        excluded_org_uids: props.excludedOrgUids,
        ...buildStatisticsDateQuery(props.dateRange),
      },
    });

    if (response.response?.status === 200) {
      data.value = response.data;
    }
  } catch (error) {
    message.error('Failed to load drafting statistics');
    logger.error('Failed to load drafting statistics:', error);
  } finally {
    loading.value = false;
  }
}

watch(props, fetchStatistics, { deep: true, immediate: true });
</script>

<template>
  <n-spin :show="loading">
    <statistics-kpi-grid v-if="data !== undefined" :tiles="kpiTiles" />
    <div v-else style="min-height: 200px" />
  </n-spin>
</template>

<style scoped></style>
